import React, { useEffect, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Prompt } from 'react-router-dom';
import ResizeObserver from 'react-resize-observer';
import IcomoonReact from 'icomoon-react';
import { useTheme } from 'emotion-theming';
import styled from '@emotion/styled';

import iconSet from '../../shared/images/teambuildr-selection.json';
import HeaderText from '../../shared/_TBComponents/HeaderText';
import Icon from '../../shared/_TBComponents/Icon';
import OnboardingModal from '../../shared/_TBComponents/OnboardingModal';
import SharedTemplatesGrid from '../sports-science/components/presentational/sharedTemplateComponents/SharedTemplatesGrid';
import SharedTemplateTriOptionMenuButton from '../sports-science/components/presentational/sharedTemplateComponents/SharedTemplateTriOptionMenuButton';
import TemplateSearchBar from '../sports-science/components/presentational/sharedTemplateComponents/TemplateSearchBar';
import NewVTDashboard from './NewVTDashboard';
import OverviewTab from './components/presentational/OverviewTab';
import AssignedCompletedTab from './components/presentational/AssignedCompletedTab';
import { TransitionGlobal } from '../../shared/GlobalStyles';
import {
  FEATURES,
  hasViewedFeature,
  createViewedFeaturePayload,
} from '../../shared/utils/onboardingStatus';
import {
  dashboardTracker,
  onboardingTracker,
} from '../../shared/utils/amplitudeHelper';
import { getModuleInfoByTitle } from '../sports-science/components/presentational/packages';
import { setViewedFeatures } from '../sports-science/ducks/sportsScienceActions';

import {
  clearActiveVTDash,
  clearVTDashData,
  clearVTDashModals,
  fetchGroups,
  fetchVTTemplates,
  setAboutVTDashModal,
  setActiveVTDash,
  setAssignedCompletedRangeMax,
  setAssignedCompletedRangeMin,
  setDeleteVTDashModal,
  setDuplicateVTDashModal,
  setEditVTDashModal,
  setExercises,
  setGroup,
  setGroups,
  setIsExerciseFiltersShowing,
  setIsNewVTDash,
  setIsVTDashDisplayed,
  setNextLocation,
  setOnboardingModal,
  setOverviewRangeMax,
  setOverviewRangeMin,
  setShareVTDashModal,
  setUnsavedVTDashModal,
  setVTDashView,
} from './ducks/volumeTrackerDashboardActions';

const Container = styled('div')`
  width: 100%;
`;

const PageWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const DashboardHeader = styled('div')`
  display: flex;
  width: 100%;
  background-color: ${(props) => props.bgColor};
  padding-top: 30px;
  padding-left: 20px;
  justify-content: space-between;
  align-items: flex-end;
`;

const MenuContainer = styled('div')`
  display: flex;
`;

const MenuItem = styled('button')`
  border: none;
  margin-left: 10px;
  margin-right: 12px;
  font-family: 'Nunito Sans';
  font-weight: 400;
  font-size: 16px;
  border-bottom: ${(props) => (props.isActive ? '3px solid #1787FB!important' : '3px solid white')};
  color: ${(props) => (!props.isActive ? '#9E9E9E' : '#444444')};
`;

const InfoWrapper = styled('div')`
  display: flex;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
  height: 20px;
  width: 20px;
  border: 2px solid #9e9e9e;
  margin-left: 10px;
  cursor: ${(props) => (props.disabled ? 'cursor' : 'pointer')};
  :hover {
    opacity: ${(props) => (props.disabled ? '1' : '.7')};
  }
  transition: ${TransitionGlobal};
  
  @media only screen and (max-width: 601px) {
    display: none;
  }
`;

const IconsAndSearch = styled('div')`
  display: flex;
  align-items: center;
`;

const HeaderWidthContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const HeaderTextAndArrow = styled('div')`
  display: flex;
  align-items: center;
  padding-bottom: 8px;
`;

const BackButtonContainer = styled('div')`
  cursor: pointer;
  margin-bottom: 4px;
`;

const TriOptionWrapper = styled('div')`
  z-index: 2;
`;

const VolumeTrackerDashboard = () => {
  const dispatch = useDispatch();
  const pageRef = useRef(null);
  const theme = useTheme();
  const searchInputRef = useRef(null);

  const [containerWidth, setContainerWidth] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedExercises, setSelectedExercises] = useState({});
  const moduleInfo = getModuleInfoByTitle('Volume Tracker');

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const accountCode = currentUser?.accountCode;

  const activeTemplate = useSelector(
    (state) => state.volumeTrackerDashboard.data.activeTemplate,
  );
  const assignedCompletedRangeMax = useSelector(
    (state) => state.volumeTrackerDashboard.data.assignedCompletedRangeMax,
  );
  const assignedCompletedRangeMin = useSelector(
    (state) => state.volumeTrackerDashboard.data.assignedCompletedRangeMin,
  );
  const bucketSize = useSelector(
    (state) => state.volumeTrackerDashboard.data.bucketSize,
  );
  const nextLocation = useSelector(
    (state) => state.volumeTrackerDashboard.data.nextLocation,
  );
  const overviewRangeMax = useSelector(
    (state) => state.volumeTrackerDashboard.data.overviewRangeMax,
  );
  const overviewRangeMin = useSelector(
    (state) => state.volumeTrackerDashboard.data.overviewRangeMin,
  );
  const volumeTrackerTemplates = useSelector(
    (state) => state.volumeTrackerDashboard.data.templates,
  );
  const isDashDisplayed = useSelector(
    (state) => state.volumeTrackerDashboard.ui.isDashDisplayed,
  );
  const isNewVTDash = useSelector(
    (state) => state.volumeTrackerDashboard.ui.isNewVTDash,
  );
  const onboardingModalShowing = useSelector(
    (state) => state.volumeTrackerDashboard.ui.onboardingModalShowing,
  );
  const viewOnly = useSelector(
    (state) => state.volumeTrackerDashboard.ui.isReadOnlyView,
  );
  const vtDashView = useSelector(
    (state) => state.volumeTrackerDashboard.ui.vtDashView,
  );

  const viewedFeatures = useSelector(
    (state) => state.sportsScience.data.viewedFeatures,
  );
  const viewedFeaturesLoading = useSelector(
    (state) => state.sportsScience.ui.viewedFeaturesLoading,
  );
  const isFeatureViewed = hasViewedFeature(
    viewedFeatures,
    FEATURES.VOLUME_TRACKER,
  );
  const isSideBarOpen = useSelector(
    (state) => state.sportsScience.data.isSideBarOpen,
  );

  const handleContainerClick = (e) => {
    if (
      e.target.id !== 'vt-dash-search-container'
      && e.target.id !== 'vt-dash-search-icon-container'
      && e.target.id !== 'vt-dash-search-container-input'
      && e.target.id !== 'vt-dash-mag-icon'
      && isSearchFocused
      && searchTerm.length === 0
    ) {
      setIsSearchFocused(false);
      if (searchInputRef?.current) {
        searchInputRef?.current?.blur();
      }
    }
  };

  const handleFocusSearch = () => {
    setIsSearchFocused(true);
    setTimeout(() => {
      if (searchInputRef?.current) {
        searchInputRef?.current?.focus();
      }
    }, 0);
  };

  const handleCreateNewDashboard = () => {
    dashboardTracker(
      'Volume Tracker',
      'Main Screen',
      'Create new dashboard clicked',
    );
    batch(() => {
      dispatch(clearActiveVTDash());
      dispatch(setGroup(null));
      dispatch(setIsNewVTDash(true));
    });
  };

  const handleTemplateView = (template) => {
    // KPI tracking
    dashboardTracker(
      'Volume Tracker',
      'Main Screen',
      'Saved dashboard opened',
    );
    const isOwner = currentUser?.id === template?.createdBy;

    batch(() => {
      dispatch(setActiveVTDash(template));
      dispatch(setGroup(null));
      dispatch(setExercises(template.exercises));

      if (template.groups.length || !isOwner) {
        dispatch(setGroups(template.groups));
      }
      dispatch(setIsVTDashDisplayed(true));
    });
  };

  const templateActions = {
    handleCreateNewDashboard,
    handleTemplateView,
    setDeleteModal: setDeleteVTDashModal,
    setAboutModal: setAboutVTDashModal,
    setEditModal: setEditVTDashModal,
    setDuplicateModal: setDuplicateVTDashModal,
    setShareModal: setShareVTDashModal,
    clearActiveTemplate: clearActiveVTDash,
  };

  const unsavedDash = isDashDisplayed && !activeTemplate;

  useEffect(() => {
    dispatch(clearVTDashModals());
    dispatch(setIsVTDashDisplayed(false));
    dispatch(setIsNewVTDash(false));
  }, []);

  useEffect(() => {
    dispatch(setOnboardingModal(false));
    if (
      !viewedFeaturesLoading
      && !isFeatureViewed
      && (containerWidth > 714 || !isSideBarOpen)
    ) {
      dispatch(setOnboardingModal(true));
      // Amplitude tracking
      onboardingTracker(
        'Modal Opened',
        'Volume Tracker',
        'Onboarding modal opened on user\'s first view of dashboard',
      );
    }
  }, [
    viewedFeaturesLoading,
    viewedFeatures,
    dispatch,
    containerWidth,
    isSideBarOpen,
  ]);

  useEffect(() => {
    if (!isDashDisplayed && currentUser) {
      dispatch(fetchGroups(currentUser.accountCode));
    }
  }, [isDashDisplayed, currentUser]);

  useEffect(() => {
    if (Object.keys(currentUser).length) {
      dispatch(fetchVTTemplates(currentUser.accountCode));
    }
  }, [currentUser]);

  useEffect(() => {
    dispatch(setIsExerciseFiltersShowing(false));
  }, [vtDashView, dispatch]);

  const handleNavigation = (location) => {
    if (unsavedDash && !nextLocation) {
      batch(() => {
        dispatch(setNextLocation(location.pathname));
        dispatch(setUnsavedVTDashModal(true));
        setSelectedExercises({});
      });
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (!viewOnly) return;
    if (vtDashView === 'overview') {
      dispatch(setOverviewRangeMin(0));
      dispatch(setOverviewRangeMax(100));
    } else {
      dispatch(setAssignedCompletedRangeMin(50));
      dispatch(setAssignedCompletedRangeMax(100));
    }
  }, [vtDashView, viewOnly, dispatch]);

  // Ensure filter buckets remain the same when switching tabs unless out of range
  const allowedRanges = {
    overview: { 5: 50, 10: 100 },
    assigned: { 5: 25, 10: 50 },
  };

  const handleMenuClick = (view) => {
    if (view === vtDashView) return;
    const isAssigned = view === 'assigned';
    const limit = allowedRanges[view][bucketSize];

    const [min, max] = isAssigned
      ? [overviewRangeMin, overviewRangeMax]
      : [assignedCompletedRangeMin, assignedCompletedRangeMax];
    const range = max - min;
    const newMin = range <= limit ? min : Math.max(0, max - limit);

    batch(() => {
      if (isAssigned) {
        dispatch(setAssignedCompletedRangeMin(newMin));
        dispatch(setAssignedCompletedRangeMax(max));
      } else {
        dispatch(setOverviewRangeMin(newMin));
        dispatch(setOverviewRangeMax(max));
      }
      dispatch(setVTDashView(view));
    });

    dashboardTracker(
      'Volume Tracker',
      isAssigned ? 'Assigned/Completed' : 'Overview',
      'Menu item clicked',
    );
  };

  const setIsVTOverviewTrue = () => {
    dispatch(setIsVTDashDisplayed(true));
  };

  const handleReturnClick = () => {
    dispatch(setIsExerciseFiltersShowing(false));
    if (isNewVTDash) {
      setSelectedExercises({});
      dispatch(setIsNewVTDash(false));
      // Amplitude tracking
      dashboardTracker(
        'Volume Tracker',
        'Back Arrow',
        'Return to main screen clicked from form',
      );
    } else if (unsavedDash) {
      batch(() => {
        dispatch(setIsNewVTDash(true));
        dispatch(setIsVTDashDisplayed(false));
      });
      // Amplitude tracking
      dashboardTracker(
        'Volume Tracker',
        'Back Arrow',
        'Return to form clicked from unsaved dashboard',
      );
    } else {
      batch(() => {
        dispatch(setIsVTDashDisplayed(!isDashDisplayed));
        dispatch(clearVTDashData());
      });
      setSelectedExercises({});
      // Amplitude tracking
      dashboardTracker(
        'Volume Tracker',
        'Main Screen',
        'Return to main screen clicked from saved dashboard template',
      );
    }
  };

  const { content } = moduleInfo;

  const handleClose = () => {
    const viewedFeaturePayload = createViewedFeaturePayload(
      accountCode,
      currentUser.id,
      FEATURES.VOLUME_TRACKER,
    );
    batch(() => {
      dispatch(setOnboardingModal(false));
      dispatch(setViewedFeatures(viewedFeaturePayload));
    });
  };

  const handleOpenOnboarding = () => {
    dispatch(setOnboardingModal(true));
  };

  return (
    <Container
      onClick={handleContainerClick}
    >
      <ResizeObserver
        onResize={(rect) => {
          setContainerWidth(rect?.width);
        }}
      />
      <Helmet>
        <title>Volume Tracker | TeamBuildr</title>
      </Helmet>
      <PageWrapper ref={pageRef}>
        <DashboardHeader bgColor={theme.colors.background[0]}>
          <HeaderWidthContainer>
            <HeaderTextAndArrow>
              {isDashDisplayed || isNewVTDash ? (
                <BackButtonContainer>
                  <IcomoonReact
                    iconSet={iconSet}
                    size={20}
                    icon='left-arrow'
                    onClick={handleReturnClick}
                  />
                </BackButtonContainer>
              ) : null}
              <HeaderText
                fontWeight='300'
                letterSpacing='normal'
                style={{
                  marginLeft: 10,
                }}
              >
                Volume Tracker
              </HeaderText>
              <InfoWrapper onClick={handleOpenOnboarding}>
                <Icon
                  icon='info'
                  size='12px'
                  styledProps={{
                    display: 'flex',
                  }}
                />
              </InfoWrapper>
            </HeaderTextAndArrow>
            {!isDashDisplayed && !isNewVTDash && (
              <MenuContainer>
                <IconsAndSearch>
                  <MenuItem>
                    <TemplateSearchBar
                      handleClick={handleFocusSearch}
                      iconContainerId='vt-dash-search-icon-container'
                      iconId='vt-dash-mag-icon'
                      id='vt-dash-search-container'
                      isFocused={isSearchFocused}
                      searchId='vt-dash-search-container-input'
                      searchRef={searchInputRef}
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                    />
                  </MenuItem>
                </IconsAndSearch>
              </MenuContainer>
            )}
            {isDashDisplayed && !isNewVTDash && (
              <MenuContainer>
                <MenuItem
                  isActive={vtDashView === 'overview'}
                  onClick={() => handleMenuClick('overview')}
                >
                  Overview
                </MenuItem>
                <MenuItem
                  isActive={vtDashView === 'assigned'}
                  onClick={() => handleMenuClick('assigned')}
                >
                  Assigned/Completed
                </MenuItem>
              </MenuContainer>
            )}
          </HeaderWidthContainer>
        </DashboardHeader>
        {!isDashDisplayed && !isNewVTDash && (
          <SharedTemplatesGrid
            searchTerm={searchTerm}
            dashboard='Volume Tracker'
            templates={volumeTrackerTemplates}
            actions={templateActions}
          />
        )}
        {isDashDisplayed && vtDashView === 'overview' && (
          <OverviewTab />
        )}
        {isDashDisplayed && vtDashView === 'assigned' && (
          <AssignedCompletedTab />
        )}
        {!isDashDisplayed && isNewVTDash && (
          <NewVTDashboard
            selectedExercises={selectedExercises}
            setSelectedExercises={setSelectedExercises}
            setIsOverview={setIsVTOverviewTrue}
            setNewDash={setIsNewVTDash}
          />
        )}
        {isDashDisplayed && (
          <TriOptionWrapper>
            <SharedTemplateTriOptionMenuButton
              dashboard='Volume Tracker'
            />
          </TriOptionWrapper>
        )}
      </PageWrapper>
      <OnboardingModal
        badgeText='GET STARTED'
        isActive={onboardingModalShowing}
        content={content}
        maxHeight={null}
        minHeight={null}
        dashName='Volume Tracker'
        onClose={handleClose}
        onPrimaryAction={handleClose}
        onSecondaryAction={handleClose}
        primaryButtonText='GET STARTED'
        secondaryButtonText='CLOSE'
        isUpgrade
      />
      <Prompt when={unsavedDash} message={handleNavigation} />
    </Container>
  );
};

export default VolumeTrackerDashboard;
